import { motion } from "framer-motion";
import { MapPin, Sparkles } from "lucide-react";
import { hero } from "../content/site";
import { Button, Container } from "./ui";
import HeroVisual from "./HeroVisual";

function scrollTo(e, href) {
  e.preventDefault();
  document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
}

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-navy pt-32 pb-20 sm:pt-40 sm:pb-28 lg:pb-32">
      {/* Background treatment: abstract city grid + glow, no stock photography */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(255,74,0,0.22),transparent)]" />
        <div className="absolute -top-24 right-[-10%] h-[420px] w-[420px] rounded-full bg-secondary/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] h-[380px] w-[380px] rounded-full bg-primary/15 blur-[120px]" />
        <svg
          className="absolute inset-x-0 bottom-0 h-40 w-full text-white/[0.04]"
          viewBox="0 0 1200 200"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M0 200V120L60 120V80H140V140H220V60H300V140H380V100H460V150H540V70H620V140H700V90H780V150H860V60H940V140H1020V100H1100V150H1200V200H0Z"
            fill="currentColor"
          />
        </svg>
      </div>

      <Container className="relative">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-10">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6 flex flex-wrap items-center gap-3"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-4 py-1.5 text-xs font-bold tracking-wide text-primary uppercase ring-1 ring-primary/30">
                <Sparkles size={13} />
                {hero.badge}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold text-white/80 uppercase ring-1 ring-white/15">
                <MapPin size={13} />
                {hero.location}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-[2.75rem] leading-[1.03] font-extrabold tracking-tight text-white sm:text-6xl lg:text-6xl xl:text-7xl"
            >
              {hero.headlineTop}
              <br />
              <span className="text-primary">{hero.headlineBottom}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 max-w-lg text-lg text-white/70 sm:text-xl"
            >
              {hero.subheadline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row"
            >
              <Button href="#waitlist" onClick={(e) => scrollTo(e, "#waitlist")} size="lg">
                {hero.primaryCta}
              </Button>
              <Button
                href="#owner-waitlist"
                onClick={(e) => scrollTo(e, "#owner-waitlist")}
                variant="outline-white"
                size="lg"
              >
                {hero.secondaryCta}
              </Button>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="mt-8 text-sm font-bold tracking-[0.2em] text-primary/90 uppercase"
            >
              {hero.tagline}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            <HeroVisual />
          </motion.div>
        </div>
      </Container>
    </section>
  );
}
